import React, { useState } from "react";
import { useWebRTC } from "../../../contexts/WebRTCContext";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, MicrophoneIcon, VideoCameraIcon, VideoCameraSlashIcon, ViewfinderCircleIcon } from "@heroicons/react/20/solid";
import { useAlert } from "../../../contexts/AlertContext";
import { SpinnerIcon } from "../../../components/loading/SpinnerIcon";
import { isMobileDevice } from "../../../helpers/common/isMobileDevice";

const MutedMicrophoneIcon = ({ className='' }) => (
  <span className={`relative inline-flex shrink-0 leading-none ${className}`}>
    <MicrophoneIcon className="h-full w-full"/>
    <span className="absolute left-1/2 top-1/2 h-0.5 w-[125%] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current"/>
  </span>
);

const CameraOnControlIcon = () => <VideoCameraIcon className="size-8 md:size-6"/>;
const CameraOffControlIcon = () => <VideoCameraSlashIcon className="size-8 md:size-6"/>;
const MicrophoneOnControlIcon = () => <MicrophoneIcon className="size-8 md:size-6"/>;
const MicrophoneOffControlIcon = () => <MutedMicrophoneIcon className="size-8 md:size-6"/>;

const getInitials = (name='') => {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?';
};

export const CameraBlock = ({ cam, camNumber, isFullscreen, onToggleFullscreen, meetMode=false }) => {
  const { registerVideoEl, registerAudioEl, setParticipantVideoEnabled, setParticipantAudioEnabled, isRecording, recordedCamIds } = useWebRTC();
  const { showAlert } = useAlert();

  const isMobile = meetMode && isMobileDevice();
  const [viewMode, setViewMode] = useState(() => isMobile ? 'object-cover' : 'object-contain');

  const isParticipant = cam.source === 'mediasoup-participant';
  const isRecorded = isRecording && recordedCamIds.includes(cam.id);
  const showVideo = !isParticipant || (cam.hasVideo && cam.videoEnabled);

  const switchViewMode = () => {
    setViewMode((viewMode) => viewMode === 'object-contain' ? 'object-cover' : 'object-contain');
  };

  const handleVideoToggle = async () => {
    try {
      await setParticipantVideoEnabled(!cam.videoEnabled);
    } catch (error) {
      if (error.code === 'recording_camera_locked') showAlert('error', 'La caméra ne peut pas être coupée pendant l\'enregistrement.');
      else showAlert('error', 'Impossible de modifier l’état de la caméra.');
    }
  };

  const handleAudioToggle = async () => {
    try {
      await setParticipantAudioEnabled(!cam.audioEnabled);
    } catch {
      showAlert('error', 'Impossible de modifier l’état du microphone.');
    }
  };

  return (
    <div className="h-full w-full">
      <div className={`relative h-full w-full overflow-hidden rounded-lg bg-perception-black-800 group ${isRecorded ? 'border-2 border-error' : ''}`}>
        <video ref={(el) => registerVideoEl(cam.id, el)} playsInline autoPlay muted className={`absolute inset-0 h-full w-full ${viewMode} object-center`}/>

        {isParticipant && <audio ref={(el) => registerAudioEl(cam.id, el)} autoPlay muted={Boolean(cam.isLocal)}/>}

        {
          isParticipant && !showVideo && !cam.isReconnecting &&
          <div className="absolute inset-0 flex items-center justify-center bg-perception-black-800">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <div className="relative size-24 md:size-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                <span className="text-3xl md:text-2xl font-semibold text-white">{getInitials(cam.displayName)}</span>
                <div className="absolute -bottom-1 -right-1 size-9 md:size-8 rounded-full bg-white/20 backdrop-blur-lg flex items-center justify-center">
                  <VideoCameraSlashIcon className="size-5 md:size-4 text-white"/>
                </div>
              </div>
              <p className="text-sm md:text-base font-medium text-white">{cam.displayName}</p>
            </div>
          </div>
        }

        {
          cam.isReconnecting &&
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-perception-black-800">
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              <SpinnerIcon/>
              <p className="text-base md:text-xl">Reconnexion en cours...</p>
            </div>
          </div>
        }

        {
          isParticipant && !cam.audioEnabled &&
          <div className="absolute z-10 top-4 left-4 flex items-center justify-center">
            <MutedMicrophoneIcon className="size-4 text-white/80"/>
          </div>
        }

        <div className="absolute top-0 left-0 right-0 flex items-center justify-between gap-4 p-4 bg-transparent backdrop-blur-none md:backdrop-blur-md md:bg-white/20 opacity-100 md:opacity-0 group-hover:opacity-100 transition no-drag">
          <div className="flex items-center min-w-0">
            {!isParticipant && <span className="text-xs font-medium text-white truncate">Caméra {camNumber} [{cam.id}]</span>}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ActionButton color="secondary" text={isFullscreen ? "Réduire" : "Etendre"} Icon={isFullscreen ? ArrowsPointingInIcon : ArrowsPointingOutIcon} size="sm" onClick={() => onToggleFullscreen(cam.id)}/>
            <ActionButton color="secondary" text={viewMode === 'object-contain' ? 'Recadrer' : 'Adapter'} Icon={ViewfinderCircleIcon} size="sm" onClick={switchViewMode}/>
          </div>
        </div>

        {
          cam.isLocal &&
          <div className="absolute z-30 bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-3 no-drag">
            <ActionButton color={cam.videoEnabled ? "secondary" : "danger"} Icon={cam.videoEnabled ? CameraOnControlIcon : CameraOffControlIcon} size="sm" className="rounded-full size-14 md:size-11 !p-0 justify-center shrink-0 transition hover:scale-105" onClick={handleVideoToggle} disabled={isRecorded}/>
            <ActionButton color={cam.audioEnabled ? "secondary" : "danger"} Icon={cam.audioEnabled ? MicrophoneOnControlIcon : MicrophoneOffControlIcon} size="sm" className="rounded-full size-14 md:size-11 !p-0 justify-center shrink-0 transition hover:scale-105" onClick={handleAudioToggle}/>
          </div>
        }
      </div>
    </div>
  );
};